import { useSelector }    from "react-redux";
import Plan               from "../../../domain/plan/Plan";
import PlanAmount         from "../../../domain/plan/PlanAmount";
import PlanDayLimit       from "../../../domain/plan/PlanDayLimit";
import PlanDescription    from "../../../domain/plan/PlanDescription";
import PlanName           from "../../../domain/plan/PlanName";
import PlanStatusType     from "../../../domain/plan/PlanStatusType";
import PlanTaxIncludeType from "../../../domain/plan/PlanTaxIncludeType";
import PlanTimeType       from "../../../domain/plan/PlanTimeType";
import { ResKaState }     from "../../../index";
import ResKaReduxAction   from "../ResKaReduxAction";

enum PlanActionType {
  INIT_PLAN = "INIT_PLAN",
  SET_PLAN_NAME = "SET_PLAN_NAME",
  SET_PLAN_DESCRIPTION = "SET_PLAN_DESCRIPTION",
  SET_PLAN_AMOUNT = "SET_PLAN_AMOUNT",
  SET_PLAN_TAX_INCLUDE_TYPE = "SET_PLAN_TAX_INCLUDE_TYPE",
  SET_PLAN_TIME_TYPE = "SET_PLAN_TIME_TYPE",
  SET_PLAN_STATUS_TYPE = "SET_PLAN_STATUS_TYPE",
  SET_PLAN_LIMIT_DAY = "SET_PLAN_LIMIT_DAY",
}

interface InitPlanAction extends ResKaReduxAction {
  plan: Plan;
}

interface SetPlanNameAction extends ResKaReduxAction {
  name: PlanName;
}

interface SetPlanDescriptionAction extends ResKaReduxAction {
  description: PlanDescription;
}

interface SetPlanAmountAction extends ResKaReduxAction {
  amount: PlanAmount;
}

interface SetPlanTaxIncludeTypeAction extends ResKaReduxAction {
  taxIncludeType: PlanTaxIncludeType;
}

interface SetPlanTimeTypeAction extends ResKaReduxAction {
  timeType: PlanTimeType;
}

interface SetPlanStatusTypeAction extends ResKaReduxAction {
  statusType: PlanStatusType;
}

interface SetPlanLimitDayAction extends ResKaReduxAction {
  day: PlanDayLimit;
}

type PlanAction =
  InitPlanAction
  | SetPlanNameAction
  | SetPlanDescriptionAction
  | SetPlanAmountAction
  | SetPlanTaxIncludeTypeAction
  | SetPlanTimeTypeAction
  | SetPlanStatusTypeAction
  | SetPlanLimitDayAction
  ;

export const initPlanAction = (plan: Plan): InitPlanAction => ({
  type: PlanActionType.INIT_PLAN,
  plan: plan,
});

export const setPlanNameAction = (name: PlanName): SetPlanNameAction => ({
  type: PlanActionType.SET_PLAN_NAME,
  name: name,
});

export const setPlanDescriptionAction = (description: PlanDescription): SetPlanDescriptionAction => ({
  type: PlanActionType.SET_PLAN_DESCRIPTION,
  description: description,
});

export const setPlanAmountAction = (amount: PlanAmount): SetPlanAmountAction => ({
  type: PlanActionType.SET_PLAN_AMOUNT,
  amount: amount,
});

export const setPlanTaxIncludeTypeAction = (taxIncludeType: PlanTaxIncludeType): SetPlanTaxIncludeTypeAction => ({
  type: PlanActionType.SET_PLAN_TAX_INCLUDE_TYPE,
  taxIncludeType: taxIncludeType,
});

export const setPlanTimeTypeAction = (timeType: PlanTimeType): SetPlanTimeTypeAction => ({
  type: PlanActionType.SET_PLAN_TIME_TYPE,
  timeType: timeType,
});

export const setPlanStatusTypeAction = (statusType: PlanStatusType): SetPlanStatusTypeAction => ({
  type: PlanActionType.SET_PLAN_STATUS_TYPE,
  statusType: statusType,
});

export const setPlanLimitDayAction = (day: PlanDayLimit): SetPlanLimitDayAction => ({
  type: PlanActionType.SET_PLAN_LIMIT_DAY,
  day: day,
});

export const planReducer = (state: Plan = new Plan(), action: PlanAction): Plan => {
  switch (action.type) {
    default:
      return state;
    case PlanActionType.INIT_PLAN:
      return (action as InitPlanAction).plan;
    case PlanActionType.SET_PLAN_NAME:
      return new Plan(
        state.id,
        (action as SetPlanNameAction).name,
        state.description,
        state.amount,
        state.taxIncludeType,
        state.timeType,
        state.statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_DESCRIPTION:
      return new Plan(
        state.id,
        state.name,
        (action as SetPlanDescriptionAction).description,
        state.amount,
        state.taxIncludeType,
        state.timeType,
        state.statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_AMOUNT:
      return new Plan(
        state.id,
        state.name,
        state.description,
        (action as SetPlanAmountAction).amount,
        state.taxIncludeType,
        state.timeType,
        state.statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_TAX_INCLUDE_TYPE:
      return new Plan(
        state.id,
        state.name,
        state.description,
        state.amount,
        (action as SetPlanTaxIncludeTypeAction).taxIncludeType,
        state.timeType,
        state.statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_TIME_TYPE:
      return new Plan(
        state.id,
        state.name,
        state.description,
        state.amount,
        state.taxIncludeType,
        (action as SetPlanTimeTypeAction).timeType,
        state.statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_STATUS_TYPE:
      return new Plan(
        state.id,
        state.name,
        state.description,
        state.amount,
        state.taxIncludeType,
        state.timeType,
        (action as SetPlanStatusTypeAction).statusType,
        state.dayLimit,
      );
    case PlanActionType.SET_PLAN_LIMIT_DAY:
      return new Plan(
        state.id,
        state.name,
        state.description,
        state.amount,
        state.taxIncludeType,
        state.timeType,
        state.statusType,
        (action as SetPlanLimitDayAction).day,
      );
  }
};

export const usePlan = (): Plan => useSelector((state: ResKaState) => state.plan);